import { TOASTER_TYPES } from 'components/core/Toaster/types';
import { NotificationTranslationKeys } from 'intl/keys';
import { logError } from 'setup/logger/logError';
import { isChannelRequestError } from 'shared/channels/errors';
import { getErrorToastHideDelay } from 'shared/utils/errorHandling';
import type { HashMap } from 'shared/types/commonView';
import {
  DEFAULT_TOASTER_HIDE_DELAY,
  DEFAULT_TOASTER_POSITION,
  normalizeToasterMessage,
} from './model';
import type { ToasterOptions, ToasterState } from './types';

export type { ToasterOptions, ToasterState, ToasterMessage } from './types';

/**
 * Small, generic pub/sub store for the toaster's visible state — no
 * knowledge of translations, logging, or any particular message content.
 * All of that lives in the plain functions below the class.
 */
class ToasterService {
  private state: ToasterState = {
    visible: false,
    position: DEFAULT_TOASTER_POSITION,
  };
  private hideTimerId: number | undefined;
  private readonly listeners = new Set<() => void>();

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): ToasterState => this.state;

  show = (options: ToasterOptions): void => {
    const {
      message,
      type,
      toasterPosition = DEFAULT_TOASTER_POSITION,
      hideDelay = DEFAULT_TOASTER_HIDE_DELAY,
    } = options;

    this.setState({
      visible: true,
      message: normalizeToasterMessage(message),
      type,
      position: toasterPosition,
    });
    this.scheduleHide(hideDelay);
  };

  hide = (): void => {
    this.clearTimer();
    this.setState({
      ...this.state,
      visible: false,
      message: undefined,
      type: undefined,
    });
  };

  private setState(state: ToasterState): void {
    this.state = state;
    this.notifyListeners();
  }

  private scheduleHide(delay: number): void {
    this.clearTimer();
    this.hideTimerId = window.setTimeout(() => {
      this.hide();
    }, delay);
  }

  private clearTimer(): void {
    if (this.hideTimerId) {
      window.clearTimeout(this.hideTimerId);
      this.hideTimerId = undefined;
    }
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener());
  }
}

export const toasterService = new ToasterService();

export function showToast(options: ToasterOptions): void {
  toasterService.show(options);
}

export function showGenericErrorToast(): void {
  toasterService.show({
    message: { id: NotificationTranslationKeys.somethingWentWrong },
    type: TOASTER_TYPES.FAILURE,
  });
}

export function showErrorToastAndLog(
  logMessage: string,
  error?: unknown
): void {
  if (logMessage) {
    logError(logMessage, error);
  }
  showGenericErrorToast();
}

export function showErrorToastWithMessage(
  messageId: string,
  params?: HashMap<string | number | null | boolean>,
  hideDelay = DEFAULT_TOASTER_HIDE_DELAY
): void {
  toasterService.show({
    message: { id: messageId, messageParams: params },
    type: TOASTER_TYPES.FAILURE,
    hideDelay,
  });
}

/**
 * A failed channel request: the server's message when it sent one
 * (`ChannelRequestError.messageId`), otherwise the generic toast and a log.
 */
export function showChannelRequestErrorToast(logMessage: string, error: unknown): void {
  const messageId = isChannelRequestError(error) ? error.messageId : null;
  if (messageId) {
    showErrorToastWithMessage(messageId, undefined, getErrorToastHideDelay(messageId));
    return;
  }
  showErrorToastAndLog(logMessage, error);
}
